import { onMounted, onBeforeUnmount, ref, nextTick, Ref } from 'vue'
import {
  isHubspotContentReady,
  monitorHubspotHydration,
  resetHubspotLoader,
} from '@/helpers/hubspotEmbed'

type UseHubspotOptions = {
  maxRetries?: number
  timeout?: number
  retryDelay?: number
  allowHardReset?: boolean
  forceInitialHardReset?: boolean
  enablePostLoadMonitor?: boolean
}

type LoadEmbedOptions = {
  suppressLoader?: boolean
}

export function useHubspotEmbed(
  containerRef: Ref<HTMLElement | null>,
  options: UseHubspotOptions = {}
) {
  const status = ref<'idle' | 'loading' | 'retrying' | 'ready' | 'empty' | 'error'>('idle')
  const isLoading = ref(true)
  const hasError = ref(false)
  const errorMessage = ref('')
  const showFallback = ref(true)
  const attempts = ref(0)

  const maxRetries = options.maxRetries ?? 2
  const timeout = options.timeout
  const retryDelay = options.retryDelay ?? 500
  const allowHardReset = options.allowHardReset ?? true
  const forceInitialHardReset = options.forceInitialHardReset ?? false
  const enablePostLoadMonitor = options.enablePostLoadMonitor ?? true

  let currentMonitorCleanup: (() => void) | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let postLoadObserver: MutationObserver | null = null
  let disappearTimer: ReturnType<typeof setTimeout> | null = null
  let loadRunId = 0
  let hardResetAttemptedForRun = false
  let hardResetAttemptsTotal = 0
  let isActive = true
  let isMounted = false
  let loadEmbed: (loadOptions?: LoadEmbedOptions) => Promise<void>

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  const cleanupMonitor = () => {
    if (currentMonitorCleanup) {
      currentMonitorCleanup()
      currentMonitorCleanup = null
    }
  }

  const stopPostLoadMonitor = () => {
    if (postLoadObserver) {
      postLoadObserver.disconnect()
      postLoadObserver = null
    }
    if (disappearTimer) {
      clearTimeout(disappearTimer)
      disappearTimer = null
    }
  }

  const startPostLoadMonitor = (container: HTMLElement) => {
    stopPostLoadMonitor()

    postLoadObserver = new MutationObserver(() => {
      if (isHubspotContentReady(container)) {
        if (disappearTimer) {
          clearTimeout(disappearTimer)
          disappearTimer = null
        }
        return
      }

      if (!disappearTimer) {
        disappearTimer = setTimeout(() => {
          disappearTimer = null
          if (!isMounted || !isActive) return
          if (isHubspotContentReady(containerRef.value)) return

          stopPostLoadMonitor()
          attempts.value = 0
          showFallback.value = true
          loadEmbed()
        }, 500)
      }
    })

    postLoadObserver.observe(container, { childList: true, subtree: true })
  }

  const stopLoading = (clearFallback = true) => {
    clearRetryTimer()
    cleanupMonitor()
    isLoading.value = false
    hasError.value = false
    errorMessage.value = ''
    if (status.value !== 'ready') {
      status.value = 'idle'
    }
    if (clearFallback) {
      showFallback.value = true
    }
  }

  const scheduleRetry = () => {
    if (!isMounted || !isActive || attempts.value > maxRetries) return

    clearRetryTimer()
    retryTimer = setTimeout(() => {
      retryTimer = null
      loadEmbed({ suppressLoader: true })
    }, retryDelay)
  }

  loadEmbed = async (loadOptions: LoadEmbedOptions = {}) => {
    if (!isMounted || !isActive) return
    const suppressLoader = loadOptions.suppressLoader ?? false
    const runId = ++loadRunId
    hardResetAttemptedForRun = false

    clearRetryTimer()
    cleanupMonitor()
    stopPostLoadMonitor()

    if (!suppressLoader) {
      isLoading.value = true
      status.value = 'loading'
    } else if (status.value !== 'ready') {
      status.value = 'retrying'
    }
    hasError.value = false
    errorMessage.value = ''
    attempts.value += 1

    try {
      const shouldForceHardReset =
        allowHardReset &&
        forceInitialHardReset &&
        attempts.value === 1 &&
        hardResetAttemptsTotal < maxRetries

      if (shouldForceHardReset) {
        hardResetAttemptsTotal += 1
      }

      await resetHubspotLoader(shouldForceHardReset ? { hardReset: true } : undefined)
      await nextTick()
    } catch {
      if (!isMounted || runId !== loadRunId) return
      showFallback.value = true
      isLoading.value = false
      status.value = attempts.value > maxRetries ? 'empty' : 'retrying'
      scheduleRetry()
      return
    }

    if (!isMounted || runId !== loadRunId) return

    if (!containerRef?.value) {
      hasError.value = true
      errorMessage.value = 'HubSpot container is not available.'
      isLoading.value = false
      status.value = 'error'
      return
    }

    const { promise, cleanup } = monitorHubspotHydration(containerRef.value, { timeout })
    currentMonitorCleanup = cleanup

    promise
      .then(() => {
        if (!isMounted || runId !== loadRunId) return
        isLoading.value = false
        showFallback.value = false
        hasError.value = false
        errorMessage.value = ''
        status.value = 'ready'
        if (enablePostLoadMonitor && containerRef.value) {
          startPostLoadMonitor(containerRef.value)
        }
      })
      .catch(async () => {
        if (!isMounted || runId !== loadRunId) return

        const container = containerRef?.value
        if (isHubspotContentReady(container)) {
          isLoading.value = false
          showFallback.value = false
          hasError.value = false
          errorMessage.value = ''
          status.value = 'ready'
          return
        }

        if (allowHardReset && !hardResetAttemptedForRun && hardResetAttemptsTotal < maxRetries) {
          hardResetAttemptedForRun = true
          hardResetAttemptsTotal += 1
          try {
            await resetHubspotLoader({ hardReset: true })
            if (!isMounted || runId !== loadRunId) return
            loadEmbed({ suppressLoader: true })
            return
          } catch {
            // Fall through to standard fallback + retry path.
          }
        }

        // Keep UX stable with fallback while retries continue in background.
        isLoading.value = false
        showFallback.value = true
        hasError.value = false
        errorMessage.value = ''
        status.value = attempts.value > maxRetries ? 'empty' : 'retrying'
        scheduleRetry()
      })
  }

  onMounted(() => {
    isMounted = true
    if (isActive) {
      loadEmbed()
    }
  })

  onBeforeUnmount(() => {
    isMounted = false
    stopLoading()
    stopPostLoadMonitor()
  })

  return {
    status,
    isLoading,
    showFallback,
    hasError,
    errorMessage,
    setActive: (active: boolean) => {
      if (isActive === active) return
      isActive = active
      if (!isMounted) return

      if (!isActive) {
        stopLoading(false)
        return
      }

      const container = containerRef?.value
      if (isHubspotContentReady(container)) {
        isLoading.value = false
        hasError.value = false
        errorMessage.value = ''
        status.value = 'ready'
        return
      }

      attempts.value = 0
      hardResetAttemptsTotal = 0
      loadEmbed()
    },
    reload: () => {
      attempts.value = 0
      hardResetAttemptsTotal = 0
      stopLoading()
      loadEmbed()
    },
  }
}
